import type {
  ApplyReuseResponse,
  ComponentValueResponse,
  DeclareExperiencesRequest,
  ProductAssessmentConditionValuesResponse,
  ProductAssessmentPreviousAnswer,
  ProductTypeAssessmentPublic,
  ProductTypesInUseResponse,
  SaveComponentValueResponse,
  StartQuizAttemptRequest,
  StartQuizAttemptResponse,
  SubmitQuestionnaireRequest,
  SubmitQuizAttemptRequest,
  SubmitQuizAttemptResponse,
} from "@repo/shared/types";
import { getWithToken, postWithToken } from "../_client";

const BASE = "/public/flow/product-assessment";

export function getPublicProductAssessmentValue(token: string) {
  return getWithToken<ComponentValueResponse>(`${BASE}/flow-instance-value`, token);
}

export function declarePublicExperiences(token: string, request: DeclareExperiencesRequest) {
  return postWithToken<SaveComponentValueResponse>(
    `${BASE}/declare`,
    token,
    request satisfies DeclareExperiencesRequest,
  );
}

/** QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered). */
export function getPublicProductAssessmentPreviousAnswer(token: string, subjectId?: string) {
  const query = subjectId ? `?subjectId=${encodeURIComponent(subjectId)}` : "";
  return getWithToken<ProductAssessmentPreviousAnswer>(`${BASE}/previous-answer${query}`, token);
}

/**
 * Apply the previous answer server-side. Mirrors the advisor's
 * `POST /internal/flow-instances/:id/reuse-previous-answer` — the merge (declarations,
 * questionnaire answers, graded quiz states) happens in the service, not the browser.
 * QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered).
 */
export function applyPublicProductAssessmentPreviousAnswer(token: string, subjectId?: string) {
  const query = subjectId ? `?subjectId=${encodeURIComponent(subjectId)}` : "";
  return postWithToken<ApplyReuseResponse>(`${BASE}/use-previous-answer${query}`, token, {});
}

export function getPublicProductAssessmentAssessments(token: string) {
  return getWithToken<ProductTypeAssessmentPublic[]>(`${BASE}/assessments`, token);
}

/** QL-698 — the `__pa_…` condition values, resolved server-side from the full record. */
export function getPublicProductAssessmentConditionValues(token: string) {
  return getWithToken<ProductAssessmentConditionValuesResponse>(`${BASE}/condition-values`, token);
}

export function getPublicProductTypesInUse(token: string) {
  return getWithToken<ProductTypesInUseResponse>(`${BASE}/product-types-in-use`, token);
}

/** QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered). */
export function startPublicQuiz(token: string, productTypeId: string, subjectId?: string) {
  return postWithToken<StartQuizAttemptResponse>(`${BASE}/quiz/start`, token, {
    productTypeId,
    ...(subjectId ? { subjectId } : {}),
  } satisfies StartQuizAttemptRequest);
}

export function submitPublicQuiz(token: string, request: SubmitQuizAttemptRequest) {
  return postWithToken<SubmitQuizAttemptResponse>(
    `${BASE}/quiz/submit`,
    token,
    request satisfies SubmitQuizAttemptRequest,
  );
}

export function submitPublicQuestionnaire(token: string, request: SubmitQuestionnaireRequest) {
  return postWithToken<SaveComponentValueResponse>(
    `${BASE}/questionnaire/submit`,
    token,
    request satisfies SubmitQuestionnaireRequest,
  );
}
